// src/pages/OrtbLintPage.jsx
// Standalone OpenRTB 2.5/2.6 structural linter — paste a bid request or bid
// response JSON payload and get a findings list grouped by severity. No
// accounts, no live traffic — just paste and read.
import React, { useMemo, useState } from "react";
import { ortbLint } from "../utils/ortbLint";

/* ── clipboard helper — same approach as InspectraNetworkView's copyToClipboard,
   reimplemented locally per task instructions (do not import from InspectraNetworkView). ── */
function copyToClipboard(text) {
    try {
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(String(text));
            return true;
        }
    } catch { /* fall through to legacy path */ }
    try {
        const ta = document.createElement("textarea");
        ta.value = String(text);
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        ta.remove();
        return true;
    } catch { return false; }
}

const LEVEL_ORDER = [
    ["error", "Errors"],
    ["warn", "Warnings"],
    ["info", "Info"],
];

const LEVEL_STYLE = {
    error: { color: "var(--error-text)" },
    warn: { color: "var(--warn-text)" },
    info: { color: "var(--muted)" },
};

function buildFindingsText(result) {
    if (!result) return "";
    const lines = [`kind: ${result.kind}`, `ok: ${result.ok}`, ""];
    for (const [level, label] of LEVEL_ORDER) {
        const rows = result.findings.filter((f) => f.level === level);
        if (!rows.length) continue;
        lines.push(`${label}:`);
        for (const f of rows) {
            lines.push(`  [${f.code}] ${f.path || "(root)"} — ${f.message}`);
        }
        lines.push("");
    }
    return lines.join("\n").trim();
}

function FindingsGroup({ level, label, rows }) {
    if (!rows.length) return null;
    return (
        <div style={{ display: "grid", gap: 4 }}>
            <div style={{ ...s.subHeading, ...LEVEL_STYLE[level] }}>{label} ({rows.length})</div>
            <div style={{ display: "grid", gap: 4 }}>
                {rows.map((f, i) => (
                    <div key={f.code + f.path + i} style={s.findingRow}>
                        <span style={s.findingCode}>{f.code}</span>
                        <span style={s.findingPath} title={f.path || "(root)"}>{f.path || "(root)"}</span>
                        <span style={{ ...s.findingMessage, ...LEVEL_STYLE[level] }}>{f.message}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

// embedded: rendered as a tab of InspectPage, which owns the paste box and
// passes its text as `value`; this page then drops its own header and input.
export default function OrtbLintPage({ value, embedded = false } = {}) {
    const [localRaw, setRaw] = useState("");
    const raw = value ?? localRaw;
    const [copyFeedback, setCopyFeedback] = useState(null);

    const parsed = useMemo(() => {
        const trimmed = raw.trim();
        if (!trimmed) return { state: "empty" };
        try {
            return { state: "ok", value: JSON.parse(trimmed) };
        } catch (e) {
            return { state: "error", error: e.message };
        }
    }, [raw]);

    const result = useMemo(() => {
        if (parsed.state !== "ok") return null;
        try {
            return ortbLint(parsed.value);
        } catch (e) {
            return { ok: false, kind: "unknown", findings: [{ path: "", level: "error", code: "unexpected_error", message: e.message }] };
        }
    }, [parsed]);

    const handleCopy = () => {
        const text = buildFindingsText(result);
        const ok = copyToClipboard(text);
        setCopyFeedback(ok ? "Copied" : "Copy failed");
        setTimeout(() => setCopyFeedback(null), 1500);
    };

    const counts = useMemo(() => {
        if (!result) return null;
        const c = { error: 0, warn: 0, info: 0 };
        for (const f of result.findings) {
            if (c[f.level] !== undefined) c[f.level] += 1;
        }
        return c;
    }, [result]);

    return (
        <div style={{ padding: embedded ? "8px 0" : 20, display: "grid", gap: 16, maxWidth: embedded ? "none" : 1100, margin: embedded ? 0 : "0 auto" }}>
            {!embedded && <>
            <div>
                <h2 style={{ margin: 0, color: "var(--ink)" }}>OpenRTB Lint</h2>
                <p style={{ margin: "4px 0 0 0", color: "var(--muted)", fontSize: 13 }}>
                    Paste an OpenRTB 2.5/2.6 bid request or bid response JSON payload and get a
                    structural findings list — missing required fields, video/banner sanity,
                    consent wiring, and eids shape.
                </p>
            </div>

            <div style={{ display: "grid", gap: 6 }}>
                <div style={s.paneLabel}>OpenRTB JSON</div>
                <textarea
                    value={raw}
                    onChange={(e) => setRaw(e.target.value)}
                    placeholder='{"id": "...", "imp": [...]} or {"id": "...", "seatbid": [...]}'
                    style={s.textarea}
                    spellCheck={false}
                />
            </div>
            </>}

            {parsed.state === "empty" && <div style={s.hint}>paste JSON to lint</div>}
            {parsed.state === "error" && (
                <div style={s.errorHint}>Invalid JSON: {parsed.error}</div>
            )}

            {result && (
                <div style={{ display: "grid", gap: 16 }}>
                    <div style={s.toolbar}>
                        <span style={s.summary}>
                            kind: {result.kind}
                            {counts && (
                                <span style={s.muted}>
                                    {" — "}
                                    {counts.error} error{counts.error === 1 ? "" : "s"} ·{" "}
                                    {counts.warn} warning{counts.warn === 1 ? "" : "s"} ·{" "}
                                    {counts.info} info
                                </span>
                            )}
                        </span>
                        <button style={s.btn} onClick={handleCopy} disabled={result.findings.length === 0}>
                            Copy findings
                        </button>
                        {copyFeedback && <span style={s.copyFeedback}>{copyFeedback}</span>}
                    </div>

                    {result.findings.length === 0 ? (
                        <div style={s.cleanState}>0 findings — structurally clean</div>
                    ) : (
                        LEVEL_ORDER.map(([level, label]) => (
                            <FindingsGroup
                                key={level}
                                level={level}
                                label={label}
                                rows={result.findings.filter((f) => f.level === level)}
                            />
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

const s = {
    paneLabel: { fontWeight: 600, fontSize: 12, color: "var(--ink)" },
    textarea: {
        minHeight: 220,
        resize: "vertical",
        fontFamily: "var(--font-mono)",
        fontSize: 12,
        lineHeight: 1.4,
        padding: 10,
        border: "1px solid var(--line)",
        borderRadius: 8,
        background: "var(--bg)",
        color: "var(--ink)",
        whiteSpace: "pre",
        overflowWrap: "normal",
    },
    toolbar: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
    btn: {
        padding: "6px 10px", fontSize: 12, border: "1px solid var(--line)",
        borderRadius: 8, background: "var(--card)", cursor: "pointer", color: "var(--ink)",
    },
    copyFeedback: { fontSize: 12, color: "var(--muted)" },
    hint: { fontSize: 12, color: "var(--muted)", fontStyle: "italic" },
    errorHint: { fontSize: 12, color: "var(--error-text)" },
    summary: { fontWeight: 600, fontSize: 13, color: "var(--ink)" },
    muted: { fontWeight: 400, color: "var(--muted)" },
    cleanState: { fontSize: 13, color: "var(--success-text)", fontWeight: 600 },
    subHeading: { fontWeight: 600, fontSize: 12, marginTop: 4 },
    findingRow: {
        display: "grid", gridTemplateColumns: "180px 220px 1fr", gap: 8,
        padding: "6px 8px", borderBottom: "1px solid var(--line)", fontSize: 12,
        alignItems: "start",
    },
    findingCode: { fontFamily: "var(--font-mono)", color: "var(--muted)" },
    findingPath: {
        fontFamily: "var(--font-mono)", color: "var(--ink)",
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
    },
    findingMessage: {},
};
